import axios from 'axios'

import { ensureFreshToken } from '@/lib/refresh-token'
import { useAuthStore } from '@/stores/auth'

const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL
const REFUSED_REFRESH_STATUSES = new Set([400, 401, 403])

export class ConnectionTicketError extends Error {
  readonly status: number
  readonly terminal: boolean

  constructor(status: number, terminal = false) {
    super(`Connection ticket request failed with status ${status}`)
    this.name = 'ConnectionTicketError'
    this.status = status
    this.terminal = terminal
  }
}

const requestTicket = (accessToken: string | null): Promise<Response> =>
  fetch(`${SIGNALR_URL}/v1/connection-tickets`, {
    method: 'POST',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'omit',
  })

const isRefusedRefresh = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response !== undefined &&
  REFUSED_REFRESH_STATUSES.has(error.response.status)

async function refreshOrGiveUp(
  sentAccessToken: string | null,
): Promise<string> {
  try {
    return await ensureFreshToken(sentAccessToken)
  } catch (error) {
    if (isRefusedRefresh(error) || !useAuthStore.getState().refreshToken) {
      throw new ConnectionTicketError(401, true)
    }
    throw error
  }
}

export async function getConnectionTicket(): Promise<string> {
  const sentAccessToken = useAuthStore.getState().accessToken
  let response = await requestTicket(sentAccessToken)

  if (response.status === 401) {
    const freshAccessToken = await refreshOrGiveUp(sentAccessToken)
    response = await requestTicket(freshAccessToken)
    if (response.status === 401) {
      throw new ConnectionTicketError(401, true)
    }
  }

  if (!response.ok) {
    throw new ConnectionTicketError(response.status)
  }

  const body = (await response.json()) as { ticket?: unknown }
  if (typeof body.ticket !== 'string' || body.ticket.length === 0) {
    throw new ConnectionTicketError(response.status)
  }

  return body.ticket
}
